// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */

import {
  centsToDollars,
  totalSellerPriceCentsForEnrollments,
} from "@outschool/business-rules";
import {
  ActivityEnrollQueryQuery,
  ActivityEnrollQueryQueryVariables,
  ActivityEnrollableTimesModal_ActivityFragment,
  ActivitySelfPacedEnrollQueryQuery,
  ActivitySelfPacedEnrollQueryQueryVariables,
  CreateEnrollmentMeetingPurchaseMutation,
  CreateEnrollmentsMutation,
  EnrollmentMeetingPurchaseQueryQuery,
  EnrollmentMeetingPurchaseQueryQueryVariables,
  SettleOrderEnrollmentMeetingPurchaseMutation,
  SettleOrderMutation,
} from "@outschool/gql-frontend-generated";
import { useCurrencyLocalization } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import {
  IntegrationCategory,
  useOrderCompleted,
  useTrackEvent,
} from "@outschool/ui-analytics";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { currentUserQuery, useSession } from "@outschool/ui-auth";
import {
  TeacherVideoModal,
  TutorMatchEnrollmentDataType,
  useTutorMatchContext,
} from "@outschool/ui-components-website";
import { LoadingModal, Modal } from "@outschool/ui-legacy-component-library";
import { useBooleanState } from "@outschool/ui-utils";
import React from "react";
import { useHistory } from "react-router";

import * as Activity from "../../shared/Activity";
import * as Routes from "../../shared/Routes";
import * as User from "../../shared/User";
import ActivityEnrollWithStripeElementsWrapper from "../components/enrolling/ActivityEnrollWithStripeElements";
import EnrollmentMeetingPurchaseWithStripeWrapper, {
  EnrollmentFromStripePurchaseOrder,
} from "../components/enrolling/EnrollmentMeetingPurchaseWithStripe";
import GiftInvitationsModal from "../components/enrolling/GiftInvitationsModal";
import { EsaMembershipSavePaymentMethod } from "../components/esaMemberships/EsaMembershipSavePaymentMethod";
import { usePaymentMethods } from "../hooks/CurrentUser";
import { useTimeZone } from "../providers/TimeZoneProvider";
import {
  ActivityEnrollQuery,
  ActivitySelfPacedEnrollQuery,
} from "../queries/ActivityEnrollQuery";
import { EnrollmentMeetingPurchaseQuery } from "../queries/EnrollmentMeetingPurchaseQuery";
import {
  useTrackNewBuyerAcquiredMutation,
  useTrackOrderCompletedMutation,
} from "../queries/TrackingMutation";
import { useAppState } from "../stores/AppStateProvider";
import { PrepayType } from "./EnrollmentMeetingPurchasePage";

type GiftEnrollmentState = {
  giftEnrollmentCount: number;
  acceptGiftSlugId: string;
};

function isUserPaidEnrollment(
  enrollment:
    | SettleOrderMutation["settleOrder"]["enrollments"][number]
    | CreateEnrollmentsMutation["createEnrollments"]["enrollments"][number],
): enrollment is SettleOrderMutation["settleOrder"]["enrollments"][number] {
  return enrollment.hasOwnProperty("isFirstPurchaseByUser");
}

// NOTE: This component is a modified version of ActivityEnrollPage.tsx.
// See if any changes made to this file need to be reflected in the original file as well.
function TutoringEnrollmentModal(
  {
    tutorMatchEnrollmentData,
    onCancel,
    onPaymentComplete,
    activityWithEnrollableTimes,
  }: {
    tutorMatchEnrollmentData: TutorMatchEnrollmentDataType;
    onCancel: () => void;
    onPaymentComplete: (
      enrollments: EnrollmentFromStripePurchaseOrder[],
    ) => Promise<void>;
    activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
  },
  ref: React.Dispatch<HTMLElement>,
) {
  const appState = useAppState();
  const history = useHistory();

  const { isEsaSession } = useSession();
  const trackeServerSideOrderCompleted = useTrackOrderCompletedMutation();
  const trackOrderCompleted = useOrderCompleted();
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  const { currencyCode } = useCurrencyLocalization();
  const trackNewBuyerAcquiredCapi = useTrackNewBuyerAcquiredMutation();
  const { timeZone } = useTimeZone();
  const { setTutorMatchEnrollmentData } = useTutorMatchContext();
  const [paymentComplete, setPaymentComplete] = useBooleanState(false);
  const [giftEnrollmentState, setGiftEnrollmentState] =
    React.useState<GiftEnrollmentState | null>(null);
  const [isRecurringEnrollment, setIsRecurringEnrollment] =
    React.useState(false);
  const [esaShowSavePayment, setEsaShowSavePayment] = React.useState(false);
  const [esaSavePaymentComplete, setEsaSavePaymentComplete] =
    React.useState(false);
  const { paymentMethods } = usePaymentMethods();
  const [defaultPaymentMethod] = paymentMethods;

  const { planActivity } = tutorMatchEnrollmentData;
  const isSelfPaced = tutorMatchEnrollmentData.activity.isSelfPaced;

  const prepayType = isEsaSession ? PrepayType.ClassWallet : null;

  const isOneOnOne =
    Activity.isOneOnOne(tutorMatchEnrollmentData.activity) &&
    !isSelfPaced &&
    !tutorMatchEnrollmentData.section;

  const startAfter = React.useMemo(() => dayjs().toDate(), []);
  const {
    data: sectionEnrollData,
    loading: sectionEnrollLoading,
    refetch: sectionEnrollRefetch,
    error: sectionEnrollError,
  } = useQueryWithPreviousData<
    ActivityEnrollQueryQuery,
    ActivityEnrollQueryQueryVariables
  >(ActivityEnrollQuery, {
    fetchPolicy: "network-only",
    skip: !tutorMatchEnrollmentData.section?.uid,
    variables: {
      sectionUid: tutorMatchEnrollmentData.section?.uid || "",
      startAfter,
    },
  });

  const {
    data: selfPacedEnrollData,
    loading: selfPacedEnrollLoading,
    refetch: selfPacedEnrollRefetch,
    error: selfPacedEnrollError,
  } = useQueryWithPreviousData<
    ActivitySelfPacedEnrollQueryQuery,
    ActivitySelfPacedEnrollQueryQueryVariables
  >(ActivitySelfPacedEnrollQuery, {
    fetchPolicy: "network-only",
    skip: !isSelfPaced,
    variables: {
      activityUid: tutorMatchEnrollmentData.activity.uid,
    },
  });

  const {
    data: enrollmentMeetingPurchaseData,
    loading: enrollmentMeetingPurchaseLoading,
    refetch: enrollmentMeetingPurchaseRefetch,
    error: enrollmentMeetingPurchaseError,
  } = useQueryWithPreviousData<
    EnrollmentMeetingPurchaseQueryQuery,
    EnrollmentMeetingPurchaseQueryQueryVariables
  >(EnrollmentMeetingPurchaseQuery, {
    fetchPolicy: "network-only",
    skip: !isOneOnOne || !planActivity?.meetingTimes?.length,
    variables: {
      activityUid: tutorMatchEnrollmentData.activity.uid,
      startTime: planActivity?.meetingTimes?.[0]?.meetingStartTime!,
    },
  });

  const { loading, data, refetch, error } = isSelfPaced
    ? {
        loading: selfPacedEnrollLoading,
        data: selfPacedEnrollData,
        refetch: selfPacedEnrollRefetch,
        error: selfPacedEnrollError,
      }
    : isOneOnOne
    ? {
        loading: enrollmentMeetingPurchaseLoading,
        data: enrollmentMeetingPurchaseData,
        refetch: enrollmentMeetingPurchaseRefetch,
        error: enrollmentMeetingPurchaseError,
      }
    : {
        loading: sectionEnrollLoading,
        data: sectionEnrollData,
        refetch: sectionEnrollRefetch,
        error: sectionEnrollError,
      };

  if (error) {
    return null;
  }

  // @ts-ignore TS(2339): Property 'enrollmentForm' does not exist on type '... Remove this comment to see the full error message
  const { enrollmentForm, currentUser } = isSelfPaced
    ? {
        ...data,
        enrollmentForm: (data as ActivitySelfPacedEnrollQueryQuery)
          .selfPacedEnrollmentForm,
      }
    : data || {};
  const {
    activity,
    section,
    currentUserEnrollments,
    conflictingEnrollments = [],
    pendingEnrollments,
    waitlistSeatsCount,
    giftEnrollments,
  } = enrollmentForm ?? {};

  const userTimeZone = User.timeZone(currentUser);

  const onCompleteFlowOneOnOne = async ({
    enrollment,
    shouldCheckForSavedPayment,
  }: {
    enrollment?: EnrollmentFromStripePurchaseOrder;
    shouldCheckForSavedPayment?: boolean;
  }) => {
    const shouldShowSavePaymentForm =
      !!currentUser?.hasEsaMembership &&
      !defaultPaymentMethod &&
      !!shouldCheckForSavedPayment;

    setEsaShowSavePayment(shouldShowSavePaymentForm);
    setPaymentComplete();
    await onPaymentComplete([enrollment as EnrollmentFromStripePurchaseOrder]);
    if (giftEnrollments?.length > 0) {
      setGiftEnrollmentState({
        giftEnrollmentCount: giftEnrollments.length,
        acceptGiftSlugId: giftEnrollments?.[0].acceptGiftSlugId,
      });
    }
  };

  const onCompleteFlow = async ({ enrollments }: $TSFixMe) => {
    const shouldShowSavePaymentForm =
      !!currentUser?.hasEsaMembership &&
      !defaultPaymentMethod &&
      !!isRecurringEnrollment;

    setEsaShowSavePayment(shouldShowSavePaymentForm);
    setPaymentComplete();
    await onPaymentComplete(enrollments);
    if (giftEnrollments?.length > 0) {
      setGiftEnrollmentState({
        giftEnrollmentCount: giftEnrollments.length,
        acceptGiftSlugId: giftEnrollments?.[0].acceptGiftSlugId,
      });
    }
  };

  const handlePaymentComplete = async ({
    enrollments,
    giftEnrollments,
    orderSlugId,
  }: {
    enrollments: SettleOrderMutation["settleOrder"]["enrollments"];
    giftEnrollments: SettleOrderMutation["settleOrder"]["giftEnrollments"];
    orderSlugId: string;
  }) => {
    // TODO: These enforcements are because of some weird behavior in
    //       ActivityEnrollQuery and currentUserQuery, probably some race condition as both queries ask for
    //       current user. Once Apollo is upgraded, try to cleanup these logics.
    if (data) {
      try {
        refetch();
      } catch (err) {
        // longstanding bug with refetch blowing up, see https://sentry.io/organizations/outschool/issues/1530195572
        // explicit log to gather more info on state
        OsPlatform.captureError(err, { extra: { propsData: data } });
      }
    }
    appState.apolloClient.query({
      query: currentUserQuery,
      fetchPolicy: "network-only",
    });
    const totalCents = totalSellerPriceCentsForEnrollments([
      ...enrollments,
      ...(giftEnrollments ?? []),
    ]);
    const dollarAmount = centsToDollars(totalCents);
    const { activity } = enrollmentForm;
    const isFirstPurchase = enrollments.some(
      e => isUserPaidEnrollment(e) && e.isFirstPurchaseByUser,
    );

    if (totalCents) {
      if (!!activity.is_ongoing_weekly) {
        trackAdEvent("Subscription Started", {
          activitySlugId: activity.slug_id,
          orderId: orderSlugId,
          revenue: dollarAmount,
          total: dollarAmount,
          value: dollarAmount,
          currency: currencyCode,
        });
      }

      trackeServerSideOrderCompleted({
        enrollmentConfirmedAt:
          enrollments.length > 0
            ? enrollments[0].confirmedAt
            : giftEnrollments?.[0].confirmedAt,
        name: activity.title,
        orderId: orderSlugId,
        price: activity.price_cents,
        productId: activity.slug_id,
        quantity: enrollments.length,
        total: totalCents,
      });

      trackOrderCompleted({
        activityListing: {
          activity,
          leader: section?.leader || activity.leader,
        },
        enrollments: [...enrollments, ...(giftEnrollments ?? [])] as any,
        section,
        user: currentUser as any,
      });
    }

    if (isFirstPurchase) {
      trackAdEvent("New Buyer Acquired", {
        revenue: dollarAmount,
        total: dollarAmount,
        currency: currencyCode,
      });
      trackNewBuyerAcquiredCapi({
        amount: totalCents,
      });
    }
  };

  const handleMeetingPurchasePaymentComplete = async ({
    enrollment,
    enrollmentMeetingPurchase,
    isFirstPurchaseByUser,
    orderSlugId,
    enrollmentDuration,
    shouldCheckForSavedPayment,
  }: {
    enrollment: SettleOrderMutation["settleOrder"]["enrollments"][number];
    enrollmentMeetingPurchase:
      | SettleOrderEnrollmentMeetingPurchaseMutation["settleOrderEnrollmentMeetingPurchase"]["enrollmentMeetingPurchase"]
      | CreateEnrollmentMeetingPurchaseMutation["createEnrollmentMeetingPurchase"]["enrollmentMeetingPurchase"];
    orderSlugId: string;
    isFirstPurchaseByUser: boolean;
    enrollmentDuration?: string; // needs to be a string for tracking
    shouldCheckForSavedPayment?: boolean;
  }) => {
    // TODO: These enforcements are because of some weird behavior in
    //       EnrollmentMeetingPurchaseQuery and currentUserQuery, probably some race condition as both queries ask for
    //       current user. Once Apollo is upgraded, try to cleanup these logics.
    if (data) {
      try {
        refetch();
      } catch (err) {
        // longstanding bug with refetch blowing up, see https://sentry.io/organizations/outschool/issues/1530195572
        // explicit log to gather more info on state
        OsPlatform.captureError(err, { extra: { propsData: data } });
      }
    }
    appState.apolloClient.query({
      query: currentUserQuery,
      fetchPolicy: "network-only",
    });
    setIsRecurringEnrollment(!!shouldCheckForSavedPayment);

    const totalCents = totalSellerPriceCentsForEnrollments([enrollment]);

    const dollarAmount = centsToDollars(totalCents);
    // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
    const { activity } = data.enrollmentMeetingPurchaseForm;

    if (totalCents) {
      trackeServerSideOrderCompleted({
        enrollmentConfirmedAt:
          enrollmentMeetingPurchase?.confirmedAt ||
          enrollment.confirmedAt ||
          enrollment.confirmed_at,
        name: activity.title,
        orderId: orderSlugId,
        price: activity.price_cents,
        productId: activity.slug_id,
        quantity: 1,
        total: totalCents,
      });

      trackOrderCompleted({
        activityListing: { activity, leader: activity.leader },
        // @ts-ignore TS(2322): Type '{ __typename?: "Enrollment" | undefined; isF... Remove this comment to see the full error message
        enrollments: [enrollment],
        section: {
          // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
          uid: "",
        },
        user: currentUser as any,
        enrollmentDuration,
      });
    }

    if (isFirstPurchaseByUser) {
      trackAdEvent("New Buyer Acquired", {
        revenue: dollarAmount,
        total: dollarAmount,
        currency: currencyCode,
      });
      trackNewBuyerAcquiredCapi({
        amount: totalCents,
      });
    }
  };

  const handleEsaSavedPaymentClose = () => {
    setEsaShowSavePayment(false);
    setEsaSavePaymentComplete(true);
  };

  const handleClose = (_e: $TSFixMe, reset = false) => {
    if (esaShowSavePayment) {
      handleEsaSavedPaymentClose();
    } else if (reset === true) {
      setTutorMatchEnrollmentData?.({
        ...tutorMatchEnrollmentData,
        planActivity: {
          ...tutorMatchEnrollmentData.planActivity,
          meetingTimes: [],
        },
      });
    } else {
      onCancel();
    }
  };

  const selfPacedStartTime = isSelfPaced
    ? planActivity?.meetingTimes?.[0]?.meetingStartTime
    : undefined;

  let currentView: React.ReactNode;

  if ((loading || enrollmentMeetingPurchaseLoading) && !activity) {
    currentView = <LoadingModal />;
  } else if (paymentComplete && !esaShowSavePayment) {
    if (!!giftEnrollmentState?.giftEnrollmentCount) {
      currentView = (
        <GiftInvitationsModal
          sectionUid={tutorMatchEnrollmentData.section?.uid}
          {...giftEnrollmentState}
        />
      );
    } else if (tutorMatchEnrollmentData?.section?.uid) {
      const leader = section?.leader || activity.leader;
      if (!leader.video) {
        onCancel();

        history.replace(
          Routes.websiteRoutes.enrollmentConfirmationPath(
            tutorMatchEnrollmentData?.section?.uid,
          ),
        );
      }
      return (
        <TeacherVideoModal
          isOpen={true}
          teacher={section?.leader || activity.leader}
          onClose={onCancel}
        />
      );
    }
  } else if (esaShowSavePayment) {
    currentView = (
      <EsaMembershipSavePaymentMethod onClose={handleEsaSavedPaymentClose} />
    );
  } else {
    if (isOneOnOne) {
      const { enrollmentMeetingPurchaseForm } =
        enrollmentMeetingPurchaseData || {};
      const {
        canPayLater,
        activity,
        learner,
        conflictingEnrollments,
        meetingRequests,
        enrollmentsForActivityAndParent,
        pendingEnrollments,
        orgTeacher,
      } = enrollmentMeetingPurchaseForm ?? {};
      currentView = (
        <EnrollmentMeetingPurchaseWithStripeWrapper
          activity={activity || tutorMatchEnrollmentData.activity}
          userTimeZone={timeZone}
          payingUser={currentUser!}
          enrollingParent={currentUser!}
          onPaymentComplete={handleMeetingPurchasePaymentComplete}
          onCompleteFlow={onCompleteFlowOneOnOne}
          onClose={handleClose}
          learner={learner}
          canPayLater={canPayLater || false}
          startTime={planActivity?.meetingTimes?.[0]?.meetingStartTime!}
          endTime={planActivity?.meetingTimes?.[0]?.meetingEndTime!}
          givenDuration={planActivity?.meetingDurationWeeks}
          conflictingEnrollments={conflictingEnrollments!}
          pendingEnrollments={pendingEnrollments!}
          enrollmentsInOtherSections={enrollmentsForActivityAndParent!}
          onMultipleMeetingsModalClose={() => {}}
          orgTeacher={orgTeacher ?? planActivity?.meetingTimes?.[0]?.orgTeacher}
          meetingRequests={meetingRequests!}
          showMultipleMeetingsModal={false}
          showCharterPurchaseComplete={false}
          prepayType={prepayType}
          actualWeeklyMeetings={0}
          showCloseButton={false}
          showCancel={false}
          resetFields={true}
          activityWithEnrollableTimes={activityWithEnrollableTimes}
          isThreeStepBooking={true}
        />
      );
    } else {
      currentView = (
        <ActivityEnrollWithStripeElementsWrapper
          activity={activity!}
          section={section}
          enrollingParentEnrollments={currentUserEnrollments}
          pendingEnrollments={pendingEnrollments}
          waitlistSeatsCount={waitlistSeatsCount}
          userTimeZone={userTimeZone}
          // @ts-ignore
          payingUser={currentUser}
          enrollingParent={currentUser}
          conflictingEnrollments={conflictingEnrollments}
          onPaymentComplete={handlePaymentComplete}
          onCompleteFlow={onCompleteFlow}
          onClose={handleClose}
          selfPacedStartTime={selfPacedStartTime}
        />
      );
    }
  }

  /*
   * We only allow the parent to close the Modal via the top button
   * in "paymentComplete" mode. Otherwise, it is possible to close
   * the payment modal while a request is in flight, breaking our some flows.
   */
  return (
    <Modal
      ref={ref}
      open={!esaSavePaymentComplete}
      onClose={() => handleClose}
      closeViaMaskClick={false}
      closeViaEscButton={false}
      hasCloseButton={paymentComplete}
      sx={{
        padding: "1.5em",
        maxWidth: "611px",
        width: "fit-content",
        minWidth: 300,
      }}
    >
      {currentView}
    </Modal>
  );
}

export default React.forwardRef(TutoringEnrollmentModal);
